import { Sort } from '@compass/client/types/common';
import { CallDirection, type Evaluation } from '@compass/client/types/evaluation';
import { EmptyState } from '@components/EmptyState';
import { GenericTable } from '@components/GenericTable';
import { useCursorPagination } from '@hooks/useCursorPagination';
import { useDebounce } from '@hooks/useDebounce';
import { useEvaluationAgents } from '@hooks/useEvaluationAgents';
import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { useStore } from '@store/index';
import { cn } from '@utils/index';
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, Ban, Flag, X, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DownloadDropdown } from '@/components/DownloadDropdown';
import { FormattedDateRelative } from '@/components/FormattedDateRelative';
import { Breadcrumb, BreadcrumbPortal, HeaderActionsPortal } from '@/components/Header';
import {
  buildMetadataParamUpdates,
  readMetadataFilterParams,
} from '@/components/metadata-filters/urlParams';
import { useMetadataFilterDefinitions } from '@/components/metadata-filters/useMetadataFilterDefinitions';
import { Badge } from '@/components/ui/badge';
import { StaticMultiSelect } from '@/components/ui/static-trigger-multi-select';
import { EVALUATION_MAX_PAGE_SIZE, EVALUATION_PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { QC_SAVED_VIEWS_CONFIG } from '@/features/savedViews/config';
import { SavedViewsMenu } from '@/features/savedViews/SavedViewsMenu';
import { useDownloadQcEvaluations } from '@/hooks/useDownloadQcEvaluations';
import type { SortState, TableColumnConfig, TableConfig } from '@/types/table';
import { formatCallDuration, getPresetDateRange, naiveDateToUtc } from '@/utils/date';
import {
  getLocalStorageItem,
  getQcFilterCache,
  saveQcFilterCache,
  setLocalStorageItem,
} from '@/utils/localStorage';
import { buildOrgRoutePath, removeOrgPrefix } from '@/utils/routes';
import { parseMultiValueParam, serializeMultiValueParam } from '@/utils/urlParams';

import { QcListFilters, type QcSearchType } from './components/QcListFilters';
import { QcScoreBadge } from './components/QcScoreBadge';
import { buildSectionScoreColumns } from './components/QcSectionScoreColumns';
import { QcStatsTabs } from './components/QcStatsTabs';
import { buildQcExportFilters, useQcPageData } from './hooks';
import { useEvaluationScorecards } from './hooks/useEvaluationScorecards';
import { useEvaluationFeedbackStatuses } from './hooks/useFeedback';
import { useStickySectionScoreColumns } from './hooks/useStickySectionScoreColumns';
import {
  collectSectionScoreColumns,
  getQcSortParamUpdates,
  isCallDurationSortUnavailable,
  parseSectionScoreSortParam,
  SECTION_SCORE_SORT_FIELD_PREFIX,
} from './sectionScores';
import {
  CALL_DIRECTION_FILTER_OPTIONS,
  getEvaluationDirection,
  getMetadataValue,
  hasActiveQcListFilters,
  parseCallDirectionsParam,
  QC_FILTER_PARAM_KEYS,
  QC_METADATA_PARAM_PREFIX,
  type QcQuickFilter,
  type QcScoreBand,
  resolveCachedQcDateRange,
  resolveQcOrganizationId,
} from './utils';

const QC_COLUMN_OPTIONS = [
  { value: 'evaluation_id', label: 'Evaluation ID', group: 'Evaluation Metadata' },
  { value: 'call_id', label: 'Call ID', group: 'Call Metadata' },
  { value: 'agent', label: 'Agent', group: 'Call Metadata' },
  { value: 'program', label: 'Program', group: 'Call Metadata' },
  { value: 'direction', label: 'Direction', group: 'Call Metadata' },
  { value: 'aggregate_score', label: 'Score', group: 'Evaluation Metadata' },
  { value: 'call_start_time', label: 'Call Start Time', group: 'Call Metadata' },
  { value: 'duration', label: 'Duration', group: 'Call Metadata' },
  { value: 'created_at', label: 'Created At', group: 'Evaluation Metadata' },
  { value: 'escalation', label: 'Escalation', group: 'Evaluation Metadata' },
  { value: 'compliance', label: 'Compliance', group: 'Evaluation Metadata' },
  { value: 'section_scores', label: 'Section Scores', group: 'Evaluation Metadata' },
] as const;

type QcColumnKey = (typeof QC_COLUMN_OPTIONS)[number]['value'];
// section_scores expands into one column per scorecard section (AQ-750), so it has no
// fixed width or column config of its own.
type BuiltInQcColumnKey = Exclude<QcColumnKey, 'section_scores'>;

const QC_COLUMN_WIDTHS: Record<BuiltInQcColumnKey, number> = {
  evaluation_id: 240,
  call_id: 240,
  agent: 320,
  program: 160,
  direction: 150,
  aggregate_score: 120,
  call_start_time: 185,
  duration: 110,
  created_at: 185,
  escalation: 130,
  compliance: 140,
};

const DEFAULT_QC_COLUMNS: QcColumnKey[] = [
  'evaluation_id',
  'agent',
  'program',
  'direction',
  'aggregate_score',
  'call_start_time',
  'escalation',
  'compliance',
];

const VALID_QC_TABS = new Set<QcQuickFilter>([
  'all',
  'needs_review',
  'escalations',
  'compliance_failure',
]);
const VALID_SCORE_BANDS = new Set<QcScoreBand>(['all', 'exemplary', 'meets', 'needs_coaching']);

const VALID_QC_COLUMNS = new Set<QcColumnKey>(QC_COLUMN_OPTIONS.map(option => option.value));

const formatPresetNaiveDate = (value: { year: number; month: number; day: number }) =>
  `${value.year}-${String(value.month + 1).padStart(2, '0')}-${String(value.day).padStart(2, '0')}`;

const TextCell: React.FC<{ value?: string | null; emphasize?: boolean }> = ({
  value,
  emphasize = false,
}) => {
  if (!value) {
    return <span className="text-muted-foreground">N/A</span>;
  }

  return (
    <span
      className={cn(
        'block min-w-0 max-w-full truncate',
        emphasize ? 'font-medium text-foreground' : 'text-foreground',
      )}
      title={value}
    >
      {value}
    </span>
  );
};

const DirectionBadge: React.FC<{ direction: 'Inbound' | 'Outbound' | 'Internal' | null }> = ({
  direction,
}) => {
  if (!direction) {
    return <span className="text-muted-foreground">--</span>;
  }

  if (direction === 'Inbound') {
    return (
      <Badge variant="outline" className="gap-1 border-transparent text-foreground">
        <ArrowDownLeft className="h-4 w-4 text-blue-700" /> {direction}
      </Badge>
    );
  }

  if (direction === 'Outbound') {
    return (
      <Badge variant="outline" className="gap-1 border-transparent text-foreground">
        <ArrowUpRight className="h-4 w-4 text-purple-700" /> {direction}
      </Badge>
    );
  }

  return (
    <Badge variant="outline" className="gap-1 border-transparent text-foreground">
      <ArrowLeftRight className="h-4 w-4 text-slate-700" /> {direction}
    </Badge>
  );
};

const FeedbackIndicator: React.FC = () => (
  <Flag
    className="h-3.5 w-3.5 shrink-0 text-notice-foreground"
    fill="currentColor"
    aria-hidden="true"
  />
);

const FlagBadge: React.FC<{ value?: boolean }> = ({ value }) => {
  if (value === true) {
    return (
      <Badge variant="outline" className="border-transparent bg-critical text-critical-foreground">
        Yes
      </Badge>
    );
  }

  if (value === false) {
    return <Badge variant="secondary">No</Badge>;
  }

  return <span className="text-muted-foreground">--</span>;
};

export const QcContainer: React.FC = () => {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { qcPageSize, setQcPageSize, getTimeZone } = useStore();
  // The picker no longer offers pages above EVALUATION_MAX_PAGE_SIZE (AQ-529), but
  // qcPageSize is persisted — a stored 100 from before this change would otherwise
  // keep requesting 100 rows with no way for the user to pick something smaller.
  const effectiveQcPageSize = Math.min(qcPageSize, EVALUATION_MAX_PAGE_SIZE);
  const timeZone = getTimeZone();

  const { programs, selectedOrganizationId, isLoadingOrgAndPrograms, unauthorizedAccess } =
    useOrgAndPrograms();
  const routeOrganizationId = orgId ? removeOrgPrefix(orgId) : null;
  const effectiveOrganizationId = resolveQcOrganizationId(
    routeOrganizationId,
    selectedOrganizationId,
  );
  const activeTabParam = searchParams.get('tab');
  const activeTab = VALID_QC_TABS.has((activeTabParam || 'all') as QcQuickFilter)
    ? ((activeTabParam || 'all') as QcQuickFilter)
    : 'all';
  const searchTypeParam = searchParams.get('search_type') || 'evaluation_id';
  const activeSearchType: QcSearchType =
    searchTypeParam === 'call_id' ? 'call_id' : 'evaluation_id';
  const callIdFilter = searchParams.get('call_id') || '';
  const evaluationIdFilter = searchParams.get('evaluation_id') || '';
  const selectedProgramIdsKey = searchParams.get('program_ids') || '';
  const selectedAgentIdsKey = searchParams.get('agent_ids') || '';
  const selectedCallDirectionsKey = searchParams.get('call_directions') || '';
  const selectedProgramIds = parseMultiValueParam(searchParams.get('program_ids'));
  const selectedAgentIds = parseMultiValueParam(searchParams.get('agent_ids'));
  const selectedCallDirections = useMemo(
    () => parseCallDirectionsParam(selectedCallDirectionsKey),
    [selectedCallDirectionsKey],
  );
  const selectedCallDirectionEnums = useMemo(
    () => selectedCallDirections.map(value => Number(value) as CallDirection),
    [selectedCallDirections],
  );
  const scoreBandParam = searchParams.get('score_band') || 'all';
  const scoreBandFilter = VALID_SCORE_BANDS.has(scoreBandParam as QcScoreBand)
    ? (scoreBandParam as QcScoreBand)
    : 'all';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';
  const onlyIneligible = searchParams.get('only_ineligible') === 'true';
  const onlyReviewed = searchParams.get('only_reviewed') === 'true';
  const defaultDateRange = useMemo(() => getPresetDateRange('last7', timeZone), [timeZone]);
  const defaultDateRangeParams = useMemo(
    () => ({
      from: formatPresetNaiveDate(defaultDateRange.from),
      to: formatPresetNaiveDate(defaultDateRange.to),
    }),
    [defaultDateRange],
  );
  const effectiveDateFrom = dateFrom || defaultDateRangeParams.from;
  const effectiveDateTo = dateTo || defaultDateRangeParams.to;
  // Scope the agent dropdown to the active date range (same mapping getCallStartTimeRange
  // applies for the list/counts queries), so GetEvaluationAgents stays index-backed
  // on organization_id + call_start_time instead of scanning the whole org (AQ-517).
  const { data: evaluationAgentsData } = useEvaluationAgents(effectiveOrganizationId || undefined, {
    callStartTimeAfter: effectiveDateFrom
      ? naiveDateToUtc(effectiveDateFrom, timeZone, 'start')
      : undefined,
    callStartTimeBefore: effectiveDateTo
      ? naiveDateToUtc(effectiveDateTo, timeZone, 'end')
      : undefined,
    ineligible: onlyIneligible,
  });
  const callStartTimeSortParam = searchParams.get('sort_by_call_start_time');
  const aggregateScoreSortParam = searchParams.get('sort_by_aggregate_score');
  const callDurationSortParam = searchParams.get('sort_by_call_duration');
  const sectionScoreSortParam = searchParams.get('sort_by_section_score');
  // Section sort > aggregate_score > call duration > call_start_time (matches backend precedence).
  const sectionScoreSort = useMemo(
    () => parseSectionScoreSortParam(sectionScoreSortParam),
    [sectionScoreSortParam],
  );
  const aggregateScoreSort =
    aggregateScoreSortParam === String(Sort.SORT_ASC)
      ? Sort.SORT_ASC
      : aggregateScoreSortParam === String(Sort.SORT_DESC)
        ? Sort.SORT_DESC
        : Sort.SORT_NONE;
  const callDurationSort =
    callDurationSortParam === String(Sort.SORT_ASC)
      ? Sort.SORT_ASC
      : callDurationSortParam === String(Sort.SORT_DESC)
        ? Sort.SORT_DESC
        : Sort.SORT_NONE;
  // Default to call_start_time DESC when no sort is selected (and no score sort is
  // active). The evaluations list endpoint is only performant when the query is
  // index-backed by a call_start_time or aggregate_score sort; sending no sort makes
  // the server fall back to an _id sort that scans the entire match set and times
  // out for high-volume orgs (see service.ListEvaluations). Newest-first is also
  // the natural default for the QC list.
  const callStartTimeSort =
    callStartTimeSortParam === String(Sort.SORT_ASC)
      ? Sort.SORT_ASC
      : callStartTimeSortParam === String(Sort.SORT_DESC)
        ? Sort.SORT_DESC
        : aggregateScoreSort !== Sort.SORT_NONE ||
            callDurationSort !== Sort.SORT_NONE ||
            sectionScoreSort
          ? Sort.SORT_NONE
          : Sort.SORT_DESC;

  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    call_id: callIdFilter,
    evaluation_id: evaluationIdFilter,
  });
  const activeSearchValue = searchValues[activeSearchType] || '';
  const debouncedSearchValue = useDebounce(activeSearchValue, 350);
  const hasActiveMetadataFilterParams = useMemo(
    () => Array.from(searchParams.keys()).some(key => key.startsWith(QC_METADATA_PARAM_PREFIX)),
    [searchParams],
  );
  // Stable primitive key for the metadata.<key> params (sorted, so param order
  // never matters) — used where a reference-stable primitive is needed instead
  // of the map itself, e.g. useCursorPagination's resetDependencies below.
  const metadataFiltersKey = useMemo(
    () =>
      Array.from(searchParams.entries())
        .filter(([rawKey]) => rawKey.startsWith(QC_METADATA_PARAM_PREFIX))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}=${value}`)
        .join('&'),
    [searchParams],
  );
  const hasActiveFilters = useMemo(() => {
    return (
      hasActiveQcListFilters({
        callIdFilter,
        evaluationIdFilter,
        selectedProgramIds,
        selectedAgentIds,
        selectedCallDirections,
        scoreBandFilter,
        activeTab,
        effectiveDateFrom,
        effectiveDateTo,
        defaultDateFrom: defaultDateRangeParams.from,
        defaultDateTo: defaultDateRangeParams.to,
      }) || hasActiveMetadataFilterParams
    );
  }, [
    activeTab,
    callIdFilter,
    defaultDateRangeParams,
    effectiveDateFrom,
    effectiveDateTo,
    evaluationIdFilter,
    hasActiveMetadataFilterParams,
    scoreBandFilter,
    selectedAgentIds,
    selectedCallDirections,
    selectedProgramIds,
  ]);

  const { currentPage, handlePageChange, getCurrentCursor } = useCursorPagination({
    pageType: 'qc',
    resetDependencies: [
      activeTab,
      activeSearchType,
      callIdFilter,
      evaluationIdFilter,
      selectedProgramIdsKey,
      selectedAgentIdsKey,
      selectedCallDirectionsKey,
      scoreBandFilter,
      dateFrom,
      dateTo,
      callStartTimeSortParam,
      aggregateScoreSortParam,
      callDurationSortParam,
      sectionScoreSortParam,
      onlyIneligible,
      onlyReviewed,
      effectiveQcPageSize,
      effectiveOrganizationId,
      timeZone,
      metadataFiltersKey,
    ],
  });

  const updateSearchParams = useCallback(
    (
      updates: Record<string, string | undefined>,
      options: {
        resetPage?: boolean;
      } = {},
    ) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);

          if (options.resetPage !== false) {
            params.delete('page');
          }

          Object.entries(updates).forEach(([key, value]) => {
            if (!value) {
              params.delete(key);
            } else {
              params.set(key, value);
            }
          });

          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const hasUrlFilters = useMemo(
    () =>
      Array.from(QC_FILTER_PARAM_KEYS).some(key => searchParams.has(key)) ||
      Array.from(searchParams.keys()).some(key => key.startsWith(QC_METADATA_PARAM_PREFIX)),
    [searchParams],
  );
  const initOrgRef = useRef<string | null>(null);

  // Single initialization effect: restore cached filters OR apply date defaults.
  // Merging these prevents a race where the date-defaults effect sets date_from/date_to
  // before the cache-restore effect runs, causing hasUrlFilters to become true and
  // blocking cache restoration on breadcrumb-back navigation.
  useEffect(() => {
    if (!effectiveOrganizationId) return;

    const isFirstInitForOrg = initOrgRef.current !== effectiveOrganizationId;
    if (isFirstInitForOrg) {
      initOrgRef.current = effectiveOrganizationId;

      // If URL already has intentional filters, skip cache restore
      if (!hasUrlFilters) {
        const cached = getQcFilterCache(effectiveOrganizationId);
        if (cached) {
          const cachedDateRange = resolveCachedQcDateRange(cached, defaultDateRangeParams);

          setSearchParams(
            prev => {
              const params = new URLSearchParams(prev);
              if (cached.tab) params.set('tab', cached.tab);
              if (cached.searchType) params.set('search_type', cached.searchType);
              if (cached.callId) params.set('call_id', cached.callId);
              if (cached.evaluationId) params.set('evaluation_id', cached.evaluationId);
              if (cached.programIds) params.set('program_ids', cached.programIds);
              if (cached.agentIds) params.set('agent_ids', cached.agentIds);
              if (cached.callDirections) params.set('call_directions', cached.callDirections);
              if (cached.scoreBand) params.set('score_band', cached.scoreBand);
              params.set('date_from', cachedDateRange.from);
              params.set('date_to', cachedDateRange.to);
              if (cached.sortByCallStartTime)
                params.set('sort_by_call_start_time', cached.sortByCallStartTime);
              if (cached.sortByAggregateScore)
                params.set('sort_by_aggregate_score', cached.sortByAggregateScore);
              if (cached.sortByCallDuration)
                params.set('sort_by_call_duration', cached.sortByCallDuration);
              if (cached.sortBySectionScore)
                params.set('sort_by_section_score', cached.sortBySectionScore);
              return params;
            },
            { replace: true },
          );
          return; // Cache provides dates — skip default date fallback
        }
      }
    }

    // Fallback: ensure date_from/date_to are always present
    if (!dateFrom || !dateTo) {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          if (!params.has('date_from')) params.set('date_from', effectiveDateFrom);
          if (!params.has('date_to')) params.set('date_to', effectiveDateTo);
          return params;
        },
        { replace: true },
      );
    }
  }, [
    hasUrlFilters,
    effectiveOrganizationId,
    dateFrom,
    dateTo,
    effectiveDateFrom,
    effectiveDateTo,
    defaultDateRangeParams,
    setSearchParams,
  ]);

  useEffect(() => {
    setSearchValues(prev => ({
      ...prev,
      call_id: callIdFilter,
      evaluation_id: evaluationIdFilter,
    }));
  }, [callIdFilter, evaluationIdFilter]);

  useEffect(() => {
    if (debouncedSearchValue !== activeSearchValue) {
      return;
    }

    const normalized = debouncedSearchValue.trim();
    const currentUrlValue = activeSearchType === 'call_id' ? callIdFilter : evaluationIdFilter;
    if (normalized === currentUrlValue) {
      return;
    }

    updateSearchParams({
      [activeSearchType]: normalized || undefined,
      // Clear the other search param when switching
      ...(activeSearchType === 'call_id' ? { evaluation_id: undefined } : { call_id: undefined }),
    });
  }, [
    activeSearchType,
    activeSearchValue,
    callIdFilter,
    evaluationIdFilter,
    debouncedSearchValue,
    updateSearchParams,
  ]);

  useEffect(() => {
    if (!effectiveOrganizationId || !dateFrom || !dateTo) return;

    saveQcFilterCache(effectiveOrganizationId, {
      tab: activeTabParam || undefined,
      searchType: searchTypeParam !== 'evaluation_id' ? searchTypeParam : undefined,
      callId: callIdFilter || undefined,
      evaluationId: evaluationIdFilter || undefined,
      programIds: selectedProgramIdsKey || undefined,
      agentIds: selectedAgentIdsKey || undefined,
      callDirections: selectedCallDirectionsKey || undefined,
      scoreBand: scoreBandParam !== 'all' ? scoreBandParam : undefined,
      dateFrom,
      dateTo,
      sortByCallStartTime: callStartTimeSortParam || undefined,
      sortByAggregateScore: aggregateScoreSortParam || undefined,
      sortByCallDuration: callDurationSortParam || undefined,
      sortBySectionScore: sectionScoreSortParam || undefined,
    });
  }, [
    effectiveOrganizationId,
    activeTabParam,
    searchTypeParam,
    callIdFilter,
    evaluationIdFilter,
    selectedProgramIdsKey,
    selectedAgentIdsKey,
    selectedCallDirectionsKey,
    scoreBandParam,
    dateFrom,
    dateTo,
    callStartTimeSortParam,
    aggregateScoreSortParam,
    callDurationSortParam,
    sectionScoreSortParam,
  ]);

  const qcColumnsStorageKey = useMemo(
    () => `compass_qc_columns_${effectiveOrganizationId || 'default'}`,
    [effectiveOrganizationId],
  );
  const [selectedColumns, setSelectedColumns] = useState<QcColumnKey[]>(DEFAULT_QC_COLUMNS);
  const hydratedColumnsRef = useRef(false);
  const debouncedSelectedColumns = useDebounce(selectedColumns, 200);

  useEffect(() => {
    if (!effectiveOrganizationId) {
      hydratedColumnsRef.current = false;
      setSelectedColumns(DEFAULT_QC_COLUMNS);
      return;
    }

    const stored = getLocalStorageItem<QcColumnKey[]>(qcColumnsStorageKey);
    if (!stored) {
      hydratedColumnsRef.current = true;
      setSelectedColumns(DEFAULT_QC_COLUMNS);
      return;
    }

    const validColumns = stored.filter((value): value is QcColumnKey =>
      VALID_QC_COLUMNS.has(value),
    );
    hydratedColumnsRef.current = true;
    setSelectedColumns(validColumns.length > 0 ? validColumns : DEFAULT_QC_COLUMNS);
  }, [effectiveOrganizationId, qcColumnsStorageKey]);

  useEffect(() => {
    if (!effectiveOrganizationId || !hydratedColumnsRef.current) {
      return;
    }

    setLocalStorageItem(qcColumnsStorageKey, debouncedSelectedColumns);
  }, [debouncedSelectedColumns, effectiveOrganizationId, qcColumnsStorageKey]);

  // Metadata filters arrive as dot-notation URL params (e.g.
  // ?metadata.call-reason=refill), either from the Advanced filters controls
  // (AQ-496) or from an analytics breakdown drill-down. Collect any
  // `metadata.<key>` param into a { key: value } map (value stays comma-joined
  // for the ListEvaluations request); the key retains its stored (slugified)
  // form.
  const metadataFilters = useMemo(() => {
    const result: Record<string, string> = {};
    for (const [rawKey, value] of searchParams.entries()) {
      if (rawKey.startsWith(QC_METADATA_PARAM_PREFIX)) {
        const key = rawKey.slice(QC_METADATA_PARAM_PREFIX.length);
        if (key) result[key] = value;
      }
    }
    return result;
  }, [searchParams]);

  // Same params as a { key: string[] } view for the Advanced filter controls.
  const selectedMetadataValues = useMemo(
    () => readMetadataFilterParams(searchParams),
    [searchParams],
  );

  const { definitions: metadataDefinitions } =
    useMetadataFilterDefinitions(effectiveOrganizationId);

  const handleMetadataFilterChange = useCallback(
    (key: string, values: string[]) => {
      updateSearchParams(buildMetadataParamUpdates({ [key]: values }));
    },
    [updateSearchParams],
  );

  // UTC call-start window mirrored to the metadata value-lookup endpoint so the
  // offered values match the rows currently in view.
  const metadataCallStartTimeAfter = useMemo(
    () => (effectiveDateFrom ? naiveDateToUtc(effectiveDateFrom, timeZone, 'start') : undefined),
    [effectiveDateFrom, timeZone],
  );
  const metadataCallStartTimeBefore = useMemo(
    () => (effectiveDateTo ? naiveDateToUtc(effectiveDateTo, timeZone, 'end') : undefined),
    [effectiveDateTo, timeZone],
  );

  const baseQueryFilters = useMemo(
    () => ({
      organizationId: effectiveOrganizationId,
      programIds: selectedProgramIds,
      callId: activeSearchType === 'call_id' ? callIdFilter || undefined : undefined,
      evaluationId:
        activeSearchType === 'evaluation_id' ? evaluationIdFilter || undefined : undefined,
      dateFrom: effectiveDateFrom,
      dateTo: effectiveDateTo,
      callDirections: selectedCallDirectionEnums,
      sortByCallStartTime: callStartTimeSort,
      sortByAggregateScore: aggregateScoreSort,
      sortByCallDuration: callDurationSort,
      sortBySectionScore: sectionScoreSort,
      pageSize: effectiveQcPageSize,
      startAfterId: getCurrentCursor(),
      timeZone,
      metadata: Object.keys(metadataFilters).length > 0 ? metadataFilters : undefined,
      onlyIneligible,
      onlyReviewed,
    }),
    [
      activeSearchType,
      callIdFilter,
      evaluationIdFilter,
      effectiveDateFrom,
      effectiveDateTo,
      getCurrentCursor,
      effectiveQcPageSize,
      effectiveOrganizationId,
      selectedProgramIds,
      selectedCallDirectionEnums,
      callStartTimeSort,
      aggregateScoreSort,
      callDurationSort,
      sectionScoreSort,
      timeZone,
      metadataFilters,
      onlyIneligible,
      onlyReviewed,
    ],
  );

  const {
    evaluations,
    error,
    hasNextPage,
    isLoading: evaluationsLoading,
    lastEvaluationId,
    tabCounts,
    tabCountsLoading,
  } = useQcPageData({
    ...baseQueryFilters,
    agentIds: selectedAgentIds,
    scoreBand: scoreBandFilter,
    quickFilter: activeTab,
  });

  // AQ-649: call duration is sorted in ClickHouse only; when it can't be served there the API
  // returns 503. Tell the user and drop back to the default sort instead of failing the page
  // (the sort is in the URL and the filter cache, so throwing would fail every reload too).
  const callDurationSortUnavailable = isCallDurationSortUnavailable(callDurationSort, error);
  useEffect(() => {
    if (!callDurationSortUnavailable) return;
    toast.error('Sorting by call duration is temporarily unavailable. Showing the default sort.');
    updateSearchParams({ sort_by_call_duration: undefined });
  }, [callDurationSortUnavailable, updateSearchParams]);

  const evaluationIds = useMemo(
    () => evaluations.map(item => item.id).filter((id): id is string => !!id),
    [evaluations],
  );
  const { feedbackStatusByEvaluationId } = useEvaluationFeedbackStatuses(
    evaluationIds,
    effectiveOrganizationId,
  );
  const sectionScoresEnabled = selectedColumns.includes('section_scores') && !onlyIneligible;
  const { scorecardsById: sectionScorecardsById, isLoading: sectionScorecardsLoading } =
    useEvaluationScorecards({
      organizationId: effectiveOrganizationId,
      evaluations,
      enabled: sectionScoresEnabled,
    });
  const liveSectionColumns = useMemo(
    () => collectSectionScoreColumns(evaluations, sectionScorecardsById),
    [evaluations, sectionScorecardsById],
  );
  // No rows (still loading, or an empty result) say nothing about the sections: keep the last ones.
  const sectionColumns = useStickySectionScoreColumns({
    organizationId: effectiveOrganizationId,
    enabled: sectionScoresEnabled,
    columns: liveSectionColumns,
    settling: evaluationsLoading || sectionScorecardsLoading || evaluations.length === 0,
  });
  const sectionScoreColumns = useMemo(
    () => buildSectionScoreColumns(sectionColumns),
    [sectionColumns],
  );

  const agentNameById = useMemo(
    () =>
      new Map(
        (evaluationAgentsData?.agents ?? [])
          .filter(agent => !!agent.user_id)
          .map(agent => [agent.user_id!, agent.name || agent.email || agent.user_id || '']),
      ),
    [evaluationAgentsData?.agents],
  );

  const programNameById = useMemo(
    () =>
      new Map(
        programs
          .filter((program): program is typeof program & { id: string } => !!program.id)
          .map(program => [program.id, program.name || program.id || '']),
      ),
    [programs],
  );

  const programOptions = useMemo(
    () =>
      programs
        .filter(program => !!program.id)
        .map(program => ({ label: program.name || program.id || '', value: program.id! }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [programs],
  );

  const agentOptions = useMemo(
    () =>
      (evaluationAgentsData?.agents ?? [])
        .filter(agent => !!agent.user_id)
        .map(agent => ({
          label: agent.name || agent.email || agent.user_id || '',
          value: agent.user_id!,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [evaluationAgentsData?.agents],
  );

  const { downloadQcEvaluations, isExporting } = useDownloadQcEvaluations();

  const exportFilters = useMemo(
    () =>
      buildQcExportFilters({
        ...baseQueryFilters,
        agentIds: selectedAgentIds,
        scoreBand: scoreBandFilter,
        quickFilter: activeTab,
      }),
    [baseQueryFilters, selectedAgentIds, scoreBandFilter, activeTab],
  );

  const allColumns = useMemo(
    (): Record<BuiltInQcColumnKey, TableColumnConfig<Evaluation>> => ({
      evaluation_id: {
        key: 'evaluation_id',
        header: 'Evaluation ID',
        width: QC_COLUMN_WIDTHS.evaluation_id,
        accessor: item => {
          const hasFeedback = !!(item.id && feedbackStatusByEvaluationId.get(item.id));

          return (
            <div className="inline-flex items-center gap-1.5">
              {item.id ? (
                <span className="font-mono text-xs">{item.id}</span>
              ) : (
                <span className="text-muted-foreground">N/A</span>
              )}
              {hasFeedback ? <FeedbackIndicator /> : null}
            </div>
          );
        },
        copyable: true,
        copyValue: item => item.id || '',
      },
      call_id: {
        key: 'call_id',
        header: 'Call ID',
        width: QC_COLUMN_WIDTHS.call_id,
        accessor: item =>
          item.call_id ? (
            <span className="font-mono text-xs">{item.call_id}</span>
          ) : (
            <span className="text-muted-foreground">N/A</span>
          ),
        copyable: true,
        copyValue: item => item.call_id || '',
      },
      agent: {
        key: 'agent',
        header: 'Agent Name',
        width: QC_COLUMN_WIDTHS.agent,
        copyable: true,
        copyValue: item => {
          const resolvedName =
            (item.agent_user_id ? agentNameById.get(item.agent_user_id) : null) ||
            getMetadataValue(item, [
              'agent_name',
              'agent',
              'representative_name',
              'rep_name',
            ])?.trim() ||
            null;
          return resolvedName || '';
        },
        accessor: item => {
          const resolvedName =
            (item.agent_user_id ? agentNameById.get(item.agent_user_id) : null) ||
            getMetadataValue(item, [
              'agent_name',
              'agent',
              'representative_name',
              'rep_name',
            ])?.trim() ||
            null;

          if (resolvedName) return <TextCell value={resolvedName} />;

          return <span className="text-muted-foreground">--</span>;
        },
      },
      program: {
        key: 'program',
        header: 'Program',
        width: QC_COLUMN_WIDTHS.program,
        accessor: item => {
          if (!item.program_id) {
            return <TextCell value={null} />;
          }

          const programName = programNameById.get(item.program_id);
          return <TextCell value={programName || item.program_id} />;
        },
      },
      direction: {
        key: 'direction',
        header: 'Call Direction',
        width: QC_COLUMN_WIDTHS.direction,
        accessor: item => <DirectionBadge direction={getEvaluationDirection(item)} />,
      },
      aggregate_score: {
        key: 'aggregate_score',
        header: 'Score',
        width: QC_COLUMN_WIDTHS.aggregate_score,
        accessor: item => <QcScoreBadge score={item.aggregate_score} />,
        sortable: true,
        sortField: 'aggregate_score',
      },
      call_start_time: {
        key: 'call_start_time',
        header: 'Call Start Time',
        width: QC_COLUMN_WIDTHS.call_start_time,
        // The default sort column (AQ-512) — the analytics/list time axis. Falls back to
        // created_at / evaluated_at for display only when a row has no call start.
        accessor: item => (
          <FormattedDateRelative
            value={item.call_start_time || item.created_at || item.evaluated_at}
          />
        ),
        sortable: true,
        sortField: 'call_start_time',
      },
      duration: {
        key: 'duration',
        header: 'Duration',
        width: QC_COLUMN_WIDTHS.duration,
        // Call duration = call_end_time - call_start_time (AQ-512 fields), formatted as
        // "5m 15s" via the shared formatCallDuration (returns "—" for missing/invalid).
        // Sorted server-side (AQ-649); missing times sort as 0s.
        accessor: item => (
          <span>{formatCallDuration(item.call_start_time, item.call_end_time)}</span>
        ),
        sortable: true,
        sortField: 'call_duration',
      },
      created_at: {
        key: 'created_at',
        header: 'Created At',
        width: QC_COLUMN_WIDTHS.created_at,
        // The evaluation's own record-created timestamp (distinct from call start).
        // Opt-in via customize-columns; not sortable — the backend deprecated
        // sort_by_created_at (AQ-512) and only orders by call_start_time / aggregate_score.
        accessor: item => <FormattedDateRelative value={item.created_at || item.evaluated_at} />,
      },
      escalation: {
        key: 'escalation',
        header: 'Escalation',
        width: QC_COLUMN_WIDTHS.escalation,
        accessor: item => <FlagBadge value={item.flags?.escalation} />,
      },
      compliance: {
        key: 'compliance',
        header: 'Compliance',
        width: QC_COLUMN_WIDTHS.compliance,
        accessor: item => {
          const failed = item.flags?.compliance_failed;
          if (failed === true)
            return (
              <Badge
                variant="outline"
                className="border-transparent bg-critical text-critical-foreground"
              >
                Fail
              </Badge>
            );
          if (failed === false)
            return (
              <Badge
                variant="outline"
                className="border-transparent bg-positive text-positive-foreground"
              >
                Pass
              </Badge>
            );
          return <span className="text-muted-foreground">--</span>;
        },
      },
    }),
    [agentNameById, feedbackStatusByEvaluationId, programNameById],
  );

  const tableConfig = useMemo((): TableConfig<Evaluation> => {
    // Only-ineligible view (AQ-653): these calls are never scored, so score /
    // escalation / compliance don't apply. Force a fixed column set that drops
    // those, ignoring the user's saved columns.
    const orderedColumns = onlyIneligible
      ? [
          allColumns.evaluation_id,
          allColumns.agent,
          allColumns.program,
          allColumns.direction,
          allColumns.call_start_time,
          allColumns.duration,
          allColumns.created_at,
        ]
      : [
          ...QC_COLUMN_OPTIONS.map(option => option.value)
            .filter(
              (columnKey): columnKey is BuiltInQcColumnKey =>
                columnKey !== 'section_scores' && selectedColumns.includes(columnKey),
            )
            .map(columnKey => allColumns[columnKey]),
          ...sectionScoreColumns,
        ];

    return {
      columns: orderedColumns,
      getRowLink: item =>
        item.id && effectiveOrganizationId
          ? buildOrgRoutePath(`/qc/${item.id}`, effectiveOrganizationId)
          : '',
      getRowKey: item => item.id || item.call_id || '',
      emptyState: {
        title: 'No Call Evaluations found',
        description: 'There are no evaluations matching your current filters.',
      },
    };
  }, [allColumns, selectedColumns, effectiveOrganizationId, onlyIneligible, sectionScoreColumns]);

  const sortState = useMemo<SortState[]>(() => {
    // One active sort at a time, in backend precedence: section > score > duration > call_start_time.
    const toDirection = (sort?: Sort) =>
      sort === Sort.SORT_ASC ? 'asc' : sort === Sort.SORT_DESC ? 'desc' : undefined;
    const active = [
      {
        field: `${SECTION_SCORE_SORT_FIELD_PREFIX}${sectionScoreSort?.section_id}`,
        direction: toDirection(sectionScoreSort?.sort),
      },
      { field: 'aggregate_score', direction: toDirection(aggregateScoreSort) },
      { field: 'call_duration', direction: toDirection(callDurationSort) },
      { field: 'call_start_time', direction: toDirection(callStartTimeSort) },
    ].find((sort): sort is SortState => !!sort.direction);
    return active ? [active] : [];
  }, [sectionScoreSort, aggregateScoreSort, callDurationSort, callStartTimeSort]);

  const handleSortChange = useCallback(
    (nextSortState: SortState[]) =>
      updateSearchParams(getQcSortParamUpdates(sortState[0], nextSortState)),
    [updateSearchParams, sortState],
  );

  const handleQcPageSizeChange = useCallback(
    (pageSize: number) => {
      setQcPageSize(pageSize);
      updateSearchParams({}, { resetPage: true });
    },
    [setQcPageSize, updateSearchParams],
  );

  const handleQcTablePageChange = useCallback(
    (newPage: number) => handlePageChange(newPage, lastEvaluationId),
    [handlePageChange, lastEvaluationId],
  );

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>Call Evaluations</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  const headerActions = (
    <HeaderActionsPortal>
      <DownloadDropdown
        onDownload={async format => {
          if (!exportFilters) return;
          await downloadQcEvaluations(exportFilters, format, agentNameById, programNameById);
        }}
        disabled={isExporting || !exportFilters}
        workflowType="pqc"
        onlyShowIcon
      />
    </HeaderActionsPortal>
  );

  if (unauthorizedAccess?.isUnauthorized) {
    return (
      <>
        {breadcrumb}
        {headerActions}
        <EmptyState
          title="Access Denied"
          description="You do not have access to this organization. Please contact your administrator if you believe this is an error."
          icon={XCircle}
          iconClassName="text-muted-foreground"
          action={{
            label: 'Go to Home',
            onClick: () => navigate('/'),
          }}
        />
      </>
    );
  }

  if (error && !callDurationSortUnavailable) {
    throw error;
  }

  return (
    <>
      {breadcrumb}
      {headerActions}

      <div className="flex h-full flex-col">
        <div className="px-6 pt-4 gap-4">
          <QcListFilters
            activeSearchType={activeSearchType}
            onSearchTypeChange={type => {
              setSearchValues(prev => ({ ...prev, [activeSearchType]: '' }));
              updateSearchParams({
                search_type: type === 'evaluation_id' ? undefined : type,
                call_id: undefined,
                evaluation_id: undefined,
              });
            }}
            searchValues={searchValues}
            onSearchValueChange={(type, value) => {
              setSearchValues(prev => ({ ...prev, [type]: value }));
            }}
            selectedProgramIds={selectedProgramIds}
            onProgramFilterChange={value =>
              updateSearchParams({ program_ids: serializeMultiValueParam(value) })
            }
            programOptions={programOptions}
            selectedAgentIds={selectedAgentIds}
            onAgentFilterChange={value =>
              updateSearchParams({ agent_ids: serializeMultiValueParam(value) })
            }
            agentOptions={agentOptions}
            selectedCallDirections={selectedCallDirections}
            onCallDirectionFilterChange={value =>
              updateSearchParams({ call_directions: serializeMultiValueParam(value) })
            }
            callDirectionOptions={CALL_DIRECTION_FILTER_OPTIONS}
            scoreBandFilter={scoreBandFilter}
            onScoreBandFilterChange={value =>
              updateSearchParams({ score_band: value === 'all' ? undefined : value })
            }
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateRangeChange={range => {
              updateSearchParams({ date_from: range.from, date_to: range.to });
            }}
            timeZone={timeZone}
            onlyIneligible={onlyIneligible}
            onOnlyIneligibleChange={next =>
              updateSearchParams(
                { only_ineligible: next ? 'true' : undefined },
                { resetPage: true },
              )
            }
            onlyReviewed={onlyReviewed}
            onOnlyReviewedChange={next =>
              updateSearchParams({ only_reviewed: next ? 'true' : undefined }, { resetPage: true })
            }
            showResetFilters={hasActiveFilters}
            onResetFilters={() => {
              setSearchValues({
                call_id: '',
                evaluation_id: '',
              });
              // Clear every active metadata.<key> param alongside the fixed keys.
              const clearMetadata = buildMetadataParamUpdates(
                Object.fromEntries(Object.keys(selectedMetadataValues).map(key => [key, []])),
              );
              updateSearchParams({
                tab: undefined,
                search_type: undefined,
                call_id: undefined,
                evaluation_id: undefined,
                program_ids: undefined,
                agent_ids: undefined,
                call_directions: undefined,
                score_band: undefined,
                only_ineligible: undefined,
                only_reviewed: undefined,
                date_from: defaultDateRangeParams.from,
                date_to: defaultDateRangeParams.to,
                ...clearMetadata,
              });
            }}
            metadataDefinitions={metadataDefinitions}
            selectedMetadataValues={selectedMetadataValues}
            onMetadataFilterChange={handleMetadataFilterChange}
            organizationId={effectiveOrganizationId}
            metadataCallStartTimeAfter={metadataCallStartTimeAfter}
            metadataCallStartTimeBefore={metadataCallStartTimeBefore}
          />

          <div className="mt-4 flex items-center justify-between gap-3">
            {onlyIneligible ? (
              // AQ-653: only-ineligible mode replaces the score-based quick-filter
              // tabs with a banner — these calls are never scored, so the tabs
              // (Needs Review / Escalations / Compliance Failure) don't apply.
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-md border bg-muted/60 px-2.5 py-1 text-sm font-medium">
                  <Ban className="h-4 w-4 text-destructive" />
                  Rating-Ineligible evaluations
                  <button
                    type="button"
                    aria-label="Show scored evaluations"
                    className="ml-0.5 rounded-sm text-muted-foreground hover:text-foreground"
                    onClick={() =>
                      updateSearchParams({ only_ineligible: undefined }, { resetPage: true })
                    }
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
                <span className="text-sm font-semibold">
                  {tabCountsLoading ? '—' : tabCounts.all}
                </span>
              </div>
            ) : (
              <QcStatsTabs
                activeFilter={activeTab}
                counts={tabCounts}
                isCountsLoading={tabCountsLoading}
                onChange={value => updateSearchParams({ tab: value === 'all' ? undefined : value })}
              />
            )}

            <div className="flex items-center gap-2">
              {!onlyIneligible && evaluations.length > 0 && (
                <StaticMultiSelect
                  options={QC_COLUMN_OPTIONS.map(option => ({
                    label: option.label,
                    value: option.value,
                    group: option.group,
                  }))}
                  selected={selectedColumns}
                  onChange={value => {
                    const nextColumns = value.filter((column): column is QcColumnKey =>
                      VALID_QC_COLUMNS.has(column as QcColumnKey),
                    );
                    setSelectedColumns(nextColumns.length > 0 ? nextColumns : DEFAULT_QC_COLUMNS);
                  }}
                  placeholder="Columns"
                  showClearAll={false}
                  size="sm"
                />
              )}
              {effectiveOrganizationId && (
                <SavedViewsMenu
                  config={QC_SAVED_VIEWS_CONFIG}
                  organizationId={effectiveOrganizationId}
                />
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden px-6">
          <div className="flex h-full min-h-0 flex-col">
            <GenericTable
              config={tableConfig}
              data={evaluations}
              loading={isLoadingOrgAndPrograms || evaluationsLoading || !effectiveOrganizationId}
              error={null}
              currentPage={currentPage}
              hasNextPage={hasNextPage}
              onPageChange={handleQcTablePageChange}
              pageSize={effectiveQcPageSize}
              pageSizeOptions={[...EVALUATION_PAGE_SIZE_OPTIONS]}
              onPageSizeChange={handleQcPageSizeChange}
              sortState={sortState}
              onSortChange={handleSortChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};
